// มอบหมายครูหอพัก — หัวหน้าฝ่ายปกครอง (27) กำหนดว่าครูคนไหนรับผิดชอบเขตไหน
//
// ครูหอพักอยู่ใต้ฝ่ายปกครอง ไม่ใช่งานหอพัก (17 = เจ้าหน้าที่ดูแลทะเบียนหอ/อาคาร) และเป็น
// ครูทั่วไปที่ไม่มีตำแหน่งของตัวเอง การผูกเขตที่หน้านี้จึงเป็นตัวให้สิทธิ์เข้าหน้าเช็คชื่อ
// หอก่อนนอนด้วย — ครูเห็นเฉพาะเขตที่ถูกผูกไว้ ไม่ใช่ทั้ง 37 เขตเต็มจอ
//
// เขตหนึ่งมีครูได้หลายคน และครูคนหนึ่งดูได้หลายเขต (เวรแทนตอนเพื่อนป่วยก็ผูกเพิ่มที่นี่)
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import Select from 'react-select';
import Swal from 'sweetalert2';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import axiosInstance from '../services/axiosInterceptor';

const API = process.env.REACT_APP_API || 'http://localhost:3002/api';

const apiError = (error) => error?.response?.data?.message || 'ลองใหม่อีกครั้ง';

const sameIds = (a, b) => [...a].sort().join(',') === [...b].sort().join(',');

export default function DisciplineDormTeachers() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const [data, setData] = useState(null);
  const [deadline, setDeadline] = useState('');
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);
  const [gender, setGender] = useState('all');

  const load = useCallback(async () => {
    try {
      const { data: res } = await axiosInstance.get(`${API}/dormitory/night-check/settings`);
      setData(res);
      setDeadline(res.settings.deadline);
      setDrafts(Object.fromEntries(res.villages.map((v) => [v.village_id, v.staff.map((s) => s.t_id)])));
    } catch (error) {
      Swal.fire('อ่านข้อมูลไม่สำเร็จ', apiError(error), 'error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const options = useMemo(
    () => (data ? data.candidates.map((c) => ({ value: c.t_id, label: c.t_name })) : []),
    [data]
  );

  // สรุปฝั่งครู: ใครดูกี่เขต — หัวหน้าถามแบบนี้บ่อยกว่า "เขตนี้มีใคร"
  const byTeacher = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    for (const v of data.villages) {
      for (const s of v.staff) {
        if (!map.has(s.t_id)) map.set(s.t_id, { ...s, villages: [] });
        map.get(s.t_id).villages.push(v.village_name + (v.village_gender === 'm' ? ' (ช)' : ' (ญ)'));
      }
    }
    return [...map.values()].sort((a, b) => a.t_name.localeCompare(b.t_name, 'th'));
  }, [data]);

  if (!data) {
    return (
      <>
        <Header onSkin={setSkin} />
        <div className="main main-app p-3 p-lg-4 text-center"><Spinner animation="border" /></div>
      </>
    );
  }

  // ครูที่ผูกไว้แต่ถูกปิดบัญชีไปแล้วยังต้องขึ้นให้เห็นและเอาออกได้
  const labelOf = (village, tId) =>
    options.find((o) => o.value === tId)?.label
    || `${village.staff.find((s) => s.t_id === tId)?.t_name || `#${tId}`} (ปิดใช้งาน)`;

  const villages = data.villages.filter((v) => gender === 'all' || v.village_gender === gender);
  const unassigned = data.villages.filter((v) => v.staff.length === 0).length;

  const saveDeadline = async () => {
    setSaving('deadline');
    try {
      await axiosInstance.put(`${API}/dormitory/night-check/settings`, { deadline });
      Swal.fire({ icon: 'success', title: 'บันทึกแล้ว', timer: 1200, showConfirmButton: false });
    } catch (error) {
      Swal.fire('บันทึกไม่สำเร็จ', apiError(error), 'error');
    } finally {
      setSaving(null);
    }
  };

  const saveVillage = async (village) => {
    setSaving(village.village_id);
    try {
      await axiosInstance.put(`${API}/dormitory/night-check/villages/${village.village_id}/staff`, {
        teacher_ids: drafts[village.village_id] || [],
      });
      await load();
    } catch (error) {
      Swal.fire('บันทึกไม่สำเร็จ', apiError(error), 'error');
    } finally {
      setSaving(null);
    }
  };

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="mb-3">
          <h4 className="main-title mb-0">มอบหมายครูหอพัก</h4>
          <div className="fs-xs text-secondary">
            ครูที่ถูกผูกกับเขตจะเห็นเมนูเช็คชื่อหอก่อนนอน และเห็นเฉพาะเขตของตัวเอง
          </div>
        </div>

        {unassigned > 0 && (
          <Alert variant="warning" className="fs-sm">
            ยังมี {unassigned} เขตที่ไม่มีครูรับผิดชอบ — แจ้งเตือนของเขตเหล่านี้จะส่งมาที่หัวหน้าฝ่ายปกครอง
          </Alert>
        )}

        <Row className="g-3">
          <Col lg={8}>
            <Card className="card-one">
              <Card.Header className="d-flex align-items-center gap-2">
                <Card.Title as="h6" className="mb-0 flex-fill">รายเขต</Card.Title>
                <Form.Select size="sm" style={{ width: 120 }} value={gender} onChange={(e) => setGender(e.target.value)}>
                  <option value="all">ทุกเขต</option>
                  <option value="m">เขตชาย</option>
                  <option value="f">เขตหญิง</option>
                </Form.Select>
              </Card.Header>
              <Card.Body className="p-0">
                <Table responsive className="mb-0 fs-sm align-middle">
                  <thead>
                    <tr><th style={{ width: 150 }}>เขต</th><th>ครูหอพัก</th><th style={{ width: 80 }}></th></tr>
                  </thead>
                  <tbody>
                    {villages.map((v) => {
                      const draft = drafts[v.village_id] || [];
                      const dirty = !sameIds(draft, v.staff.map((s) => s.t_id));
                      return (
                        <tr key={v.village_id}>
                          <td>
                            {v.village_name}{' '}
                            <Badge bg={v.village_gender === 'm' ? 'primary' : 'danger'}>{v.village_gender === 'm' ? 'ชาย' : 'หญิง'}</Badge>
                            <div className="fs-xs text-secondary">{v.houses} หลัง · {v.students} คน</div>
                          </td>
                          <td style={{ minWidth: 260 }}>
                            <Select
                              isMulti
                              options={options}
                              value={draft.map((id) => ({ value: id, label: labelOf(v, id) }))}
                              onChange={(sel) => setDrafts((prev) => ({ ...prev, [v.village_id]: (sel || []).map((s) => s.value) }))}
                              placeholder="พิมพ์ชื่อครู..."
                              noOptionsMessage={() => 'ไม่พบ'}
                              classNamePrefix="rs"
                              menuPortalTarget={document.body}
                              styles={{ menuPortal: (base) => ({ ...base, zIndex: 1060 }) }}
                            />
                          </td>
                          <td>
                            <Button size="sm" variant={dirty ? 'primary' : 'outline-secondary'}
                              disabled={!dirty || saving === v.village_id} onClick={() => saveVillage(v)}>
                              บันทึก
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            <Card className="card-one mb-3">
              <Card.Body>
                <Form.Label className="fs-sm mb-1">ต้องเช็คชื่อให้เสร็จก่อน</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control type="time" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
                  <Button onClick={saveDeadline} disabled={saving === 'deadline' || !deadline}>บันทึก</Button>
                </div>
                <div className="fs-xs text-secondary mt-2">
                  เลยเวลานี้แล้วหลังไหนยังไม่มีใครยืนยัน ระบบแจ้งครูหอพักของเขตนั้น
                  คืนที่ทั้งโรงเรียนยังไม่มีใครยืนยันเลยจะไม่แจ้ง เพื่อไม่ให้เตือนทุกคืนช่วงปิดภาค
                </div>
              </Card.Body>
            </Card>

            <Card className="card-one">
              <Card.Header><Card.Title as="h6" className="mb-0">รายครู ({byTeacher.length} คน)</Card.Title></Card.Header>
              <ul className="list-group list-group-flush fs-sm">
                {byTeacher.length === 0 ? (
                  <li className="list-group-item text-secondary">ยังไม่ได้มอบหมายใคร</li>
                ) : byTeacher.map((t) => (
                  <li key={t.t_id} className="list-group-item">
                    <div className={t.active ? '' : 'text-decoration-line-through text-secondary'}>{t.t_name}</div>
                    <div className="fs-xs text-secondary">{t.villages.join(', ')}</div>
                  </li>
                ))}
              </ul>
            </Card>
          </Col>
        </Row>

        <Footer />
      </div>
    </>
  );
}
